<template>
  <div class="natcash-test-view">
    <div class="page-header">
      <div>
        <h1 class="page-title">Test de connexion Natcash</h1>
        <p class="page-subtitle">Joignabilité des serveurs à travers le VPN</p>
      </div>
      <v-btn color="secondary" depressed :loading="loading" :disabled="loading" @click="runTest">
        <v-icon left small>mdi-refresh</v-icon>
        Retester
      </v-btn>
    </div>

    <div class="content-container">
      <v-card flat class="readout" :class="'is-' + state">
        <div class="verdict">
          <span class="dot" aria-hidden="true"></span>
          <div class="verdict-body">
            <p class="verdict-title">{{ title }}</p>
            <p class="verdict-sub">{{ subtitle }}</p>
          </div>
        </div>

        <ul v-if="targets.length" class="rows">
          <li v-for="t in targets" :key="t.host + ':' + t.port" class="row">
            <span class="row-name">{{ t.label }}</span>
            <span class="row-addr mono">{{ t.host }}:{{ t.port }}</span>
            <span class="row-value mono" :class="t.reachable ? 'v-ok' : 'v-down'">
              {{ t.reachable ? t.latency_ms + ' ms' : 'aucune réponse' }}
            </span>
          </li>
        </ul>

        <p v-for="t in failures" :key="'e-' + t.host + t.port" class="failure mono">
          {{ t.label }} — {{ t.error }}
        </p>

        <p v-if="context" class="context mono">{{ context }}</p>
      </v-card>
    </div>
  </div>
</template>

<script>
export default {
  name: 'NatcashTestView',
  data: () => ({
    loading: false,
    hasRun: false,
    reachable: false,
    targets: [],
    environment: null,
    checkedAt: null,
    timeout: null,
    apiFailed: false,
  }),
  computed: {
    // pending | ok | partial | down | api
    state: function () {
      if (this.loading || !this.hasRun) return 'pending'
      if (this.apiFailed) return 'api'
      if (this.reachable) return 'ok'
      return this.failures.length === this.targets.length ? 'down' : 'partial'
    },
    failures: function () {
      return this.targets.filter(function (t) { return !t.reachable })
    },
    title: function () {
      switch (this.state) {
        case 'pending': return this.loading ? 'Test en cours' : 'Aucun test lancé'
        case 'ok': return 'Natcash répond'
        case 'partial': return 'Un service ne répond pas'
        case 'down': return 'Natcash ne répond pas'
        // API muette : le test n'a pas eu lieu, Natcash n'est pas en cause.
        default: return "L'API n'a pas répondu"
      }
    },
    subtitle: function () {
      var time = this.checkedAtLabel
      switch (this.state) {
        case 'pending':
          return this.loading ? 'Connexion aux serveurs Natcash' : 'Lancez un test pour connaître l\'état de la liaison'
        case 'ok':
          return this.targets.length + ' services joignables · ' + time
        case 'partial':
          return this.failures.length + ' sur ' + this.targets.length + ' injoignable · ' + time
        case 'down':
          return 'Aucun service ne répond · ' + time
        default:
          return 'Le test n\'a pas été lancé. Le VPN n\'est pas en cause.'
      }
    },
    context: function () {
      if (!this.environment) return null
      return this.environment + ' · délai d\'attente ' + this.timeout + ' s'
    },
    checkedAtLabel: function () {
      if (!this.checkedAt) return '—'
      return new Date(this.checkedAt).toLocaleTimeString('fr-FR')
    },
  },
  methods: {
    runTest: function () {
      var self = this
      this.loading = true
      this.apiFailed = false

      this.$axios.get('/dashboard/natcash-health')
        .then(function (response) {
          var payload = (response.data && response.data.data) || {}
          self.reachable = payload.reachable === true
          self.targets = payload.targets || []
          self.environment = payload.environment || null
          self.checkedAt = payload.checked_at || null
          self.timeout = payload.timeout_s || null
          self.hasRun = true
        })
        .catch(function (error) {
          console.log(error)
          self.apiFailed = true
          self.reachable = false
          self.targets = []
          self.environment = null
          self.checkedAt = null
          self.hasRun = true
        })
        .then(function () {
          self.loading = false
        })
    },
  },
  mounted: function () {
    this.runTest()
  },
}
</script>

<style scoped>
/* .page-header porte son propre rembourrage ; le contenu doit fournir le sien
   sinon il colle au menu lateral. On suit les memes valeurs que l'en-tete a
   chaque taille d'ecran, pour que le titre et la carte restent alignes. */
.content-container {
  padding: 0 24px 24px;
}

@media (max-width: 599px) {
  .content-container {
    padding: 0 16px 16px;
  }
}

/* Carte volontairement etroite et peu rembourree : le relevé tient en un coup d'oeil. */
.readout {
  max-width: 480px;
  padding: 18px 20px 16px;
  border-radius: 8px;
}

/* La couleur d'etat n'existe que sur le point et les valeurs. */
.readout { color: #94a3b8; }
.is-ok { color: #12805c; }
.is-partial { color: #b4740f; }
.is-down { color: #c7362f; }

.verdict {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.dot {
  flex: none;
  width: 8px;
  height: 8px;
  margin-top: 7px;
  border-radius: 50%;
  background: currentColor;
}

.is-pending .dot { animation: pulse 1.4s ease-in-out infinite; }

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}

@media (prefers-reduced-motion: reduce) {
  .is-pending .dot { animation: none; }
}

.verdict-body { min-width: 0; }

.verdict-title {
  margin: 0;
  font-size: 1.0625rem;
  font-weight: 600;
  letter-spacing: -0.012em;
  line-height: 1.3;
  color: rgba(0, 0, 0, 0.87);
}

.theme--dark .verdict-title { color: #fff; }

.verdict-sub {
  margin: 2px 0 0;
  font-size: 0.75rem;
  opacity: 0.75;
}

.rows {
  list-style: none;
  margin: 14px 0 0;
  padding: 0;
}

.row {
  display: flex;
  align-items: baseline;
  gap: 12px;
  padding: 7px 0;
  border-top: 1px solid rgba(128, 141, 160, 0.16);
}

/* min-width et non width : « retrait » se coupait en plein mot a 54px. */
.row-name {
  flex: none;
  min-width: 62px;
  white-space: nowrap;
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.55);
}

.theme--dark .row-name { color: rgba(255, 255, 255, 0.6); }

/* Une IP coupee en plein milieu est illisible : on ne la casse jamais.
   Sur mobile c'est la rangee entiere qui passe a la ligne (voir plus bas). */
.row-addr {
  flex: 1 1 auto;
  min-width: 0;
  white-space: nowrap;
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.5);
}

.theme--dark .row-addr { color: rgba(255, 255, 255, 0.5); }

/* « aucune reponse » est bien plus long que « 56 ms » : sans nowrap la
   mise en page se disloque au moment precis ou la panne survient. */
.row-value {
  flex: none;
  white-space: nowrap;
  font-size: 0.8125rem;
  font-weight: 600;
}

.v-ok { color: #12805c; }
.v-down { color: #c7362f; }

.failure {
  margin: 8px 0 0;
  font-size: 0.6875rem;
  color: #c7362f;
  overflow-wrap: anywhere;
}

.context {
  margin: 12px 0 0;
  font-size: 0.6875rem;
  opacity: 0.5;
}

.mono {
  font-family: ui-monospace, 'SF Mono', 'Roboto Mono', Menlo, Consolas, monospace;
  font-variant-numeric: tabular-nums;
}

@media (max-width: 600px) {
  .row { flex-wrap: wrap; gap: 2px 12px; }
  .row-name { min-width: 0; }
  .row-value { margin-left: auto; }
}
</style>
